/** Presentation only: native radio change events continue to the host variant picker. */
class VoltOptionSelector extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    if (!this.details || !this.summary) return;
    if (!this.hasAttribute('data-enhanced')) this.details.open = false;
    this.setAttribute('data-enhanced', '');
    this.panel = this.querySelector('dialog');
    this.scroller = this.querySelector('.volt-option-selector__scroll');
    this.mobile = matchMedia('(max-width: 749px)');
    this.mobile.addEventListener('change', () => this.close(true, true), { signal });
    this.summary.addEventListener('click', (event) => {
      event.preventDefault();
      this.keyboard = event.detail === 0;
      if (this.details.open) this.close(true); else this.open({ keyboard: this.keyboard });
    }, { signal });
    this.panel.addEventListener('pointerdown', (event) => {
      // Native mouse focus can scroll a clipped radio and reposition its sticky dropdown before click.
      // Keep focus stable; the label still dispatches the native radio click/change.
      if (event.pointerType === 'mouse' && event.button === 0 && !this.panel.matches(':modal') &&
          event.target.closest('label')?.querySelector('input[type=radio]')) event.preventDefault();
    }, { signal });
    this.panel.addEventListener('cancel', (event) => { event.preventDefault(); this.close(true); }, { signal });
    this.panel.addEventListener('click', (event) => {
      if (event.target !== this.panel || !this.panel.matches(':modal')) return;
      const rect = this.panel.getBoundingClientRect();
      if (event.clientY < rect.top || event.clientX < rect.left || event.clientX > rect.right) this.close(true);
    }, { signal });
    this.refresh();
    this.addEventListener('change', (event) => {
      if (!(event.target instanceof HTMLInputElement) || event.target.type !== 'radio') return;
      this.refresh();
      this.close(true);
    }, { signal });
    this.addEventListener('keydown', (event) => {
      this.keyboard = true;
      if (event.key === 'Escape' && this.details.open) {
        event.preventDefault();
        event.stopPropagation();
        this.close(true);
      } else if (event.target === this.summary && event.key === 'ArrowDown') {
        event.preventDefault();
        this.open();
      } else if (event.target instanceof HTMLInputElement && this.details.open) {
        const inputs = [...this.querySelectorAll('input[type=radio]:not(:disabled)')];
        const index = inputs.indexOf(event.target);
        if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? inputs.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + inputs.length) % inputs.length;
          this.focusOption(inputs[next]);
        } else if (event.key === 'Enter') {
          event.preventDefault();
          event.target.click();
          this.close(true);
        }
      }
    }, { signal });
    document.addEventListener('pointerdown', (event) => {
      if (!this.contains(event.target) && !this.panel.matches(':modal')) this.close(false);
    }, { signal });
    this.addEventListener('focusout', () => {
      queueMicrotask(() => {
        if (this.isConnected && !this.panel.matches(':modal') && !this.contains(document.activeElement)) this.close(false);
      });
    }, { signal });
  }
  disconnectedCallback() {
    this.abort?.abort();
    this.animation?.cancel();
    this.unlock();
    if (this.panel?.matches(':modal')) this.panel.close();
  }
  unlock() {
    if (this.scrollOverflow !== undefined) {
      document.documentElement.style.overflow = this.scrollOverflow;
      this.scrollOverflow = undefined;
    }
  }
  motion(opening) {
    const from = this.animation?.playState === 'running' ? getComputedStyle(this.panel).transform : (opening ? 'translateY(100%)' : 'translateY(0)');
    this.animation?.cancel();
    if (!this.panel.matches(':modal') || this.keyboard || this.dataset.motion === 'off' || matchMedia('(prefers-reduced-motion: reduce)').matches) return Promise.resolve();
    this.animation = this.panel.animate([
      { transform: from },
      { transform: opening ? 'translateY(0)' : 'translateY(100%)' },
    ], { duration: opening ? 220 : 200, easing: 'cubic-bezier(.22,1,.36,1)' });
    return this.animation.finished.catch(() => {});
  }
  positionPanel() {
    if (!this.details?.open || this.panel.matches(':modal')) return;
    const anchor = this.getBoundingClientRect();
    const viewport = window.visualViewport;
    const top = viewport?.offsetTop || 0;
    const bottom = top + (viewport?.height || window.innerHeight);
    const gap = 12;
    const margin = 12;
    if (anchor.bottom <= top + margin || anchor.top >= bottom - margin) {
      this.close(false, true);
      return;
    }
    const above = Math.max(0, anchor.top - top - gap - margin);
    const below = Math.max(0, bottom - anchor.bottom - gap - margin);
    const wanted = Math.min(356, this.scroller.scrollHeight + 26);
    const placement = below >= wanted || below >= above ? 'below' : 'above';
    this.dataset.placement = placement;
    this.style.setProperty('--option-selector-available-height', `${Math.min(356, placement === 'below' ? below : above)}px`);
  }
  focusOption(target, preserveScroll = false) {
    target?.focus({ preventScroll: true });
    const row = target?.closest('label');
    if (!row || preserveScroll) return;
    const item = row.getBoundingClientRect();
    const viewport = this.scroller.getBoundingClientRect();
    if (item.top < viewport.top) this.scroller.scrollTop -= viewport.top - item.top;
    else if (item.bottom > viewport.bottom) this.scroller.scrollTop += item.bottom - viewport.bottom;
  }
  refresh() {
    const checked = this.querySelector('input[type="radio"]:checked');
    const label = this.querySelector('[data-option-summary]');
    const selectedLabel = checked?.dataset.optionLabel || checked?.value;
    if (label) {
      label.textContent = selectedLabel || this.dataset.optionName;
      this.summary.setAttribute('aria-label', checked ? `${this.dataset.optionName}: ${selectedLabel}` : this.dataset.optionName);
    }
  }
  open({ keyboard = true, preserveScroll = false } = {}) {
    if (this.closing) return;
    this.keyboard = keyboard;
    this.details.open = true;
    if (this.hasAttribute('data-mobile-sheet') && this.mobile.matches && !this.panel.matches(':modal')) {
      this.panel.close();
      this.scrollOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.panel.showModal();
      this.motion(true);
    } else {
      this.panel.open = true;
      this.positionPanel();
    }
    const target = this.querySelector('input:checked') || this.querySelector('input:not(:disabled)');
    this.focusOption(target, preserveScroll);
  }
  async close(restoreFocus, instant = false) {
    if (!this.details?.open) return;
    if (this.closing) return this.closing;
    this.closing = (async () => {
      if (!instant) await this.motion(false);
      else this.animation?.cancel();
      if (this.panel.matches(':modal')) this.panel.close();
      this.unlock();
      this.details.open = false;
      this.panel.open = true;
      if (restoreFocus && this.isConnected) this.summary.focus({ preventScroll: true });
    })();
    await this.closing;
    this.closing = null;
  }

}
if (!customElements.get('volt-option-selector')) customElements.define('volt-option-selector', VoltOptionSelector);
